import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Table, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtDate } from '../lib/format.js';
import NewOrderModal from '../components/NewOrderModal.jsx';

const STATUS_LABELS = {
  draft:             { label: 'Entwurf',         variant: 'secondary' },
  pending_approval:  { label: 'Freigabe ausstehend', variant: 'warning' },
  approved:          { label: 'Freigegeben',     variant: 'info' },
  fulfilled:         { label: 'Erfuellt',        variant: 'success' },
  cancelled:         { label: 'Storniert',       variant: 'danger' },
  suspended:         { label: 'Pausiert',        variant: 'dark' }
};

export default function OrdersPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const canCreate = auth.can('orders', 'create');

  const [status, setStatus] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [showModal, setShowModal] = useState(false);

  const orders = useQuery({
    queryKey: ['orders', { status, createdFrom, createdTo }],
    queryFn: () => api.get('/orders', {
      params: {
        status: status || undefined,
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined,
        limit: 200
      }
    }).then(r => r.data.items)
  });

  const customers = useQuery({
    queryKey: ['customers-min'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items)
  });

  const customerById = Object.fromEntries((customers.data || []).map(c => [c.id, c]));

  const filtered = (orders.data || []).filter(o => {
    if (!customerSearch) return true;
    const c = customerById[o.customer_id];
    if (!c) return false;
    const q = customerSearch.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.customer_number?.toLowerCase().includes(q);
  });

  return (
    <>
      <PageHeader
        title="Bestellungen"
        subtitle="Manuelle Bestellungen + Approval-Workflow fuer Self-Orders"
        actions={canCreate && (
          <Button onClick={() => setShowModal(true)}>
            <Icon icon="tabler:plus" className="me-1" />Neue Bestellung
          </Button>
        )}
      />

      <Card className="card-flat mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small mb-1">Status</Form.Label>
              <Form.Select value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">Alle</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-1">Kunde (Suche)</Form.Label>
              <Form.Control
                type="text" placeholder="Name oder K-Nummer"
                value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Erstellt ab</Form.Label>
              <Form.Control type="date" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Erstellt bis</Form.Label>
              <Form.Control type="date" value={createdTo} onChange={e => setCreatedTo(e.target.value)} />
            </Col>
            <Col md={2} className="text-end">
              <Button variant="outline-secondary" size="sm"
                onClick={() => { setStatus(''); setCustomerSearch(''); setCreatedFrom(''); setCreatedTo(''); }}>
                <Icon icon="tabler:filter-off" className="me-1" />Filter zuruecksetzen
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="card-flat">
        <Card.Body className="p-0">
          {orders.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {orders.data && filtered.length === 0 && (
            <div className="empty-state">
              <Icon icon="tabler:shopping-cart-off" className="empty-icon" />
              <div>Keine Bestellungen mit diesen Filtern.</div>
            </div>
          )}
          {filtered.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead><tr>
                <th>Bestellnummer</th><th>Kunde</th><th>Status</th>
                <th>Erstellt</th><th>Entschieden</th><th>Items</th><th></th>
              </tr></thead>
              <tbody>
                {filtered.map(o => {
                  const c = customerById[o.customer_id];
                  const meta = STATUS_LABELS[o.status] || { label: o.status, variant: 'secondary' };
                  const itemCount = o.configurator_input?.items?.length || 0;
                  return (
                    <tr key={o.id} style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/orders/${o.id}`)}>
                      <td><span className="fw-semibold">{o.order_number}</span></td>
                      <td>
                        {c ? (
                          <Link to={`/customers/${c.id}`} onClick={e => e.stopPropagation()}>
                            {c.name}
                          </Link>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td><Badge bg="" className={`badge-soft-${meta.variant}`}>{meta.label}</Badge></td>
                      <td className="small">{fmtDate(o.created_at)}</td>
                      <td className="small">{o.decided_at ? fmtDate(o.decided_at) : '—'}</td>
                      <td className="small">{itemCount}</td>
                      <td className="text-end pe-3">
                        <Button size="sm" variant="outline-secondary"
                          onClick={(e) => { e.stopPropagation(); navigate(`/orders/${o.id}`); }}>
                          <Icon icon="tabler:eye" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <NewOrderModal show={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}
